import { Head, useForm, usePage } from '@inertiajs/react'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Card, CardTitle, Field, Label, Input, Select, Button, Text, ErrorMessage } from '@/components/ui'
import { useTranslations, LANGUAGE_NAMES } from '@/lib/i18n'
import type { SharedProps } from '@/types'

interface SettingsProps {
  profile: { name: string; email: string; locale: string | null }
  errors: Record<string, string>
}

function SettingsIndex({ profile, errors }: SettingsProps) {
  const { t } = useTranslations()
  const { props } = usePage<SharedProps>()

  const profileForm = useForm({ name: profile.name, locale: profile.locale ?? props.locale })
  const passwordForm = useForm({ current_password: '', password: '', password_confirmation: '' })

  function saveProfile(event: React.FormEvent) {
    event.preventDefault()
    profileForm.transform((data) => ({ user: data }))
    profileForm.patch('/settings')
  }

  function savePassword(event: React.FormEvent) {
    event.preventDefault()
    passwordForm.transform((data) => ({ user: data }))
    passwordForm.patch('/settings', { onSuccess: () => passwordForm.reset() })
  }

  return (
    <>
      <Head title={t('settings.title')} />
      <PageHeading title={t('settings.title')} />

      <div className="mt-8 flex max-w-2xl flex-col gap-6">
        <Card>
          <CardTitle>{t('settings.profile')}</CardTitle>
          <Text className="mt-1">{t('settings.profile_desc')}</Text>
          <form onSubmit={saveProfile} className="mt-4 flex flex-col gap-5">
            <Field>
              <Label>{t('settings.name')}</Label>
              <Input value={profileForm.data.name} onChange={(e) => profileForm.setData('name', e.target.value)} />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>
            <Field>
              <Label>{t('settings.email')}</Label>
              <Input type="email" value={profile.email} disabled />
            </Field>
            <Field>
              <Label>{t('settings.language')}</Label>
              <Select value={profileForm.data.locale} onChange={(e) => profileForm.setData('locale', e.target.value)}>
                {props.available_locales.map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGE_NAMES[code] ?? code}
                  </option>
                ))}
              </Select>
            </Field>
            <div>
              <Button type="submit" color="brand" disabled={profileForm.processing}>
                {t('settings.save_profile')}
              </Button>
            </div>
          </form>
        </Card>

        <Card>
          <CardTitle>{t('settings.password')}</CardTitle>
          <form onSubmit={savePassword} className="mt-4 flex flex-col gap-5">
            <Field>
              <Label>{t('settings.current_password')}</Label>
              <Input
                type="password"
                autoComplete="current-password"
                value={passwordForm.data.current_password}
                onChange={(e) => passwordForm.setData('current_password', e.target.value)}
              />
              {errors.current_password && <ErrorMessage>{errors.current_password}</ErrorMessage>}
            </Field>
            <Field>
              <Label>{t('settings.new_password')}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={passwordForm.data.password}
                onChange={(e) => passwordForm.setData('password', e.target.value)}
              />
              {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
            </Field>
            <Field>
              <Label>{t('settings.confirm_password')}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={passwordForm.data.password_confirmation}
                onChange={(e) => passwordForm.setData('password_confirmation', e.target.value)}
              />
            </Field>
            <div>
              <Button type="submit" color="brand" disabled={passwordForm.processing}>
                {t('settings.change_password')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  )
}

SettingsIndex.layout = withAppLayout

export default SettingsIndex
